import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { toast } from '@/lib/v2/components';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import { useCampaignsService } from '@/src/infrastructure/Protocol/Campaigns/useCampaignsService';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import { AUTOMATION_PATHS } from '@/src/modules/AutomationModuleNew/constants';

import { useValidNameResource } from '@/modules/CampaignsModule/hooks/useValidNameResource';
import { EResourceType } from '@/modules/CampaignsModule/interfaces/CampaignActions';
import {
  ECampaignsTypeOptions,
  ICreateEditCampaigns,
  IDuplicateCampaigns,
  IDuplicateCampaignsV1,
} from '@/modules/CampaignsModule/interfaces/Campaigns';

const TRANSLATE_MODAL_DELETE = 'RECURRENT_MESSAGE.DELETE_RECURRENT.TOAST_MESSAGES';
const TOAST_MESSAGES = `RECURRENT_MESSAGE.CREATE_RECURRENT.TOAST_MESSAGES`;
const TRANSLATE_MODAL_DUPLICATE = 'RECURRENT_MESSAGE.DUPLICATE_RECURRENT.TOAST_MESSAGES';

export const useActionsRecurringMessage = () => {
  const { t } = useTranslation();
  const service = useCampaignsService();
  const serviceV1 = useService();
  const duplicateCampaignV2 = useFeatureFlag('duplicateCampaign');
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { fetchServiceValidName, loadingValidation: isValidationLoading } = useValidNameResource();

  const deleteRecurrent = useCallback(
    async (campaignID: number[]) => {
      if (!campaignID) return;
      const isSingle: boolean = campaignID?.length === 1;

      setIsLoading(true);

      const { statusOK } = await serviceV1.deleteV1Campaigns(campaignID);

      if (!statusOK) {
        toast({
          title: isSingle
            ? t(`${TRANSLATE_MODAL_DELETE}.ERROR.SINGLE.title`)
            : t(`${TRANSLATE_MODAL_DELETE}.ERROR.MULTIPLE.title`),
          body: isSingle
            ? t(`${TRANSLATE_MODAL_DELETE}.ERROR.SINGLE.message`)
            : t(`${TRANSLATE_MODAL_DELETE}.ERROR.MULTIPLE.message`),
          variant: 'error',
        });
        return;
      }

      toast({
        title: isSingle
          ? t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.SINGLE.title`)
          : t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.MULTIPLE.title`),
        body: isSingle
          ? t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.SINGLE.message`)
          : t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.MULTIPLE.message`),
        variant: 'success',
      });

      setIsLoading(false);
    },
    [serviceV1, t]
  );

  const editRecurringMessage = useCallback(
    async (name: string, campaignId: number) => {
      setIsLoading(true);

      const campaignRecurrentData: ICreateEditCampaigns = {
        name: name.replace(/\s+/g, ' ').trim(),
        campaignStatesId: 1,
        campaignTypeId: Number(ECampaignsTypeOptions.AUTOMATIC),
        flowId: null,
        autoConfirmation: 1,
        notifyGroupId: 0,
        strategyId: null,
        strategyName: null,
        goal: null,
        startDate: null,
        endDate: null,
        campaignId,
      };

      const campaignRecurrentId = { campaignId };

      const { statusOK, recurrentCreated } = await service.createCampaignRecurrent({
        campaignRecurrent: {
          ...campaignRecurrentData,
          ...campaignRecurrentId,
        },
      });

      let title, message;

      if (statusOK) {
        title = t(`${TOAST_MESSAGES}.EDIT.SUCCESS.title`);
        message = t(`${TOAST_MESSAGES}.EDIT.SUCCESS.message`);
      } else {
        title = t(`${TOAST_MESSAGES}.EDIT.ERROR.title`);
        message = t(`${TOAST_MESSAGES}.EDIT.ERROR.message`);
      }

      toast({
        title,
        body: message,
        variant: statusOK ? 'success' : 'error',
      });

      setIsLoading(false);

      return recurrentCreated;
    },
    [service, t]
  );

  const handleValidation = useCallback(
    async (value: string) => {
      const isValidName = await fetchServiceValidName({
        name: value,
        resource: EResourceType.CAMPAIGN,
      });
      return {
        isValidInput: isValidName,
        message: t('RECURRENT_MESSAGE.CREATE_RECURRENT.existCampaignRecurrent'),
      };
    },
    [fetchServiceValidName, t]
  );

  const duplicateRecurrent = useCallback(
    async (name: string, campaignId: number) => {
      if (!campaignId) return;

      setIsLoading(true);

      const campaignPayload: IDuplicateCampaigns = {
        name: name.replace(/\s+/g, ' ').trim(),
        campaignId: Number(campaignId),
        strategyId: null,
        strategyName: '',
        goal: '',
        startDate: null,
        endDate: null,
      };

      const campaignPayloadV1: IDuplicateCampaignsV1 = {
        campaignId: Number(campaignId),
        name: name,
      };

      let statusOK, campaignIdDuplicate, hasLegacyActions;

      if (duplicateCampaignV2) {
        ({
          statusOK,
          campaignId: campaignIdDuplicate,
          hasLegacyActions,
        } = await service.duplicateCampaigns(campaignPayload));
      } else {
        ({ statusOK, campaignIdDuplicate } = await serviceV1.duplicateV1Campaigns(
          campaignPayloadV1
        ));
      }

      setIsLoading(false);

      const title = t(`${TRANSLATE_MODAL_DUPLICATE}.${statusOK ? 'SUCCESS' : 'ERROR'}.title`);
      const body = statusOK
        ? t(
            `${TRANSLATE_MODAL_DUPLICATE}.SUCCESS.${
              hasLegacyActions ? 'messageLegacyActions' : 'message'
            }`
          )
        : t(`${TRANSLATE_MODAL_DUPLICATE}.ERROR.message`);

      let variant: 'error' | 'success' | 'warning' = 'error';

      if (statusOK) {
        variant = hasLegacyActions ? 'warning' : 'success';
      }

      toast({
        title,
        body,
        variant,
      });

      return campaignIdDuplicate;
    },
    [duplicateCampaignV2, service, serviceV1, t]
  );

  const handleSubmitData = useCallback(
    async (name: string) => {
      setIsLoading(true);

      const campaignRecurrentData: ICreateEditCampaigns = {
        name: name.replace(/\s+/g, ' ').trim(),
        campaignStatesId: 1,
        campaignTypeId: Number(ECampaignsTypeOptions.AUTOMATIC),
        flowId: null,
        autoConfirmation: 1,
        notifyGroupId: 0,
        strategyId: null,
        strategyName: null,
        goal: null,
        startDate: null,
        endDate: null,
      };

      const { statusOK, recurrentCreated } = await service.createCampaignRecurrent({
        campaignRecurrent: {
          ...campaignRecurrentData,
        },
      });

      setIsLoading(false);

      let title, message;
      if (statusOK) {
        title = t(`${TOAST_MESSAGES}.EDIT.SUCCESS.title`);
        message = t(`${TOAST_MESSAGES}.EDIT.SUCCESS.message`);
        const { campaignId } = recurrentCreated;
        navigate(
          `/v2/${AUTOMATION_PATHS.MODULE_PATH}/${AUTOMATION_PATHS.RECURRENCES_PATH}/${campaignId}`
        );
      } else {
        title = t(`${TOAST_MESSAGES}.EDIT.ERROR.title`);
        message = t(`${TOAST_MESSAGES}.EDIT.ERROR.message`);
      }

      toast({
        title,
        body: message,
        variant: statusOK ? 'success' : 'error',
      });
    },
    [navigate, service, t]
  );

  const deleteAction = useCallback(
    async (actionsId: number[]) => {
      const isSingle: boolean = actionsId?.length === 1;
      setIsLoading(true);

      const { statusOK } = await serviceV1.deleteV1Actions(actionsId);

      if (!statusOK) {
        toast({
          title: isSingle
            ? t(`${TRANSLATE_MODAL_DELETE}.ERROR.SINGLE.title`)
            : t(`${TRANSLATE_MODAL_DELETE}.ERROR.MULTIPLE.title`),
          body: isSingle
            ? t(`${TRANSLATE_MODAL_DELETE}.ERROR.SINGLE.message`)
            : t(`${TRANSLATE_MODAL_DELETE}.ERROR.MULTIPLE.message`),
          variant: 'error',
        });
        return;
      }

      toast({
        title: isSingle
          ? t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.SINGLE.title`)
          : t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.MULTIPLE.title`),
        body: isSingle
          ? t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.SINGLE.message`)
          : t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.MULTIPLE.message`),
        variant: 'success',
      });

      setIsLoading(false);
    },
    [serviceV1, t]
  );

  return {
    isLoading,
    deleteRecurrent,
    editRecurringMessage,
    duplicateRecurrent,
    handleSubmitData,
    handleValidation,
    deleteAction,
    isValidationLoading,
  };
};
